import CourtPortalService from "api/CourtPortalService";
import fileService from "api/fileService";
import pluginService from "api/pluginService";
import KeysContext from "contexts/KeysContext";
import { IAttachment } from "dto/IAttachment";
import { IKey } from "dto/IKey";
import { ItemKind } from "dto/IKindItems";
import { useContext, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useDownloadFileStore } from "store/downloadFile.store";
import { blobToBase64 } from "utils/blobToBase64";
import { decryptFile } from "utils/decryptFile";
import { getBlob } from "utils/getBlob";
import { getUserId } from "utils/getUserId";
import {
  isAllowedAttachment,
  isArchiveAttachment,
} from "utils/isAllowedAttachment";
import { ENotificationType, notification } from "utils/notification";
import { readFileFromBase64 } from "utils/readFileFromBase64";

function usePDF(chatToken?: string, chatId?: string, withoutPlugin?: boolean) {
  const { mutate: getFile } = fileService.useGetFile();
  const { mutate: getContactFromChatFile } =
    fileService.useGetContactMessageChatFile();
  const { mutate: getCourtPortalPreview } = CourtPortalService.usePreview();
  const { mutate: getKsefPreview } = CourtPortalService.usePreviewPdf();
  // bez AuthProvidera (aplikacja EBI, ebi/) useAuth() zwraca undefined
  const auth = useAuth();
  const userId = getUserId(auth?.user);
  const { keys } = useContext(KeysContext);
  const { mutate: tryPreview } = pluginService.usePreview();
  const { mutate: getPluginVersion } = pluginService.useGetVersionMutated();
  const [isLoading, setIsLoading] = useState(false);
  const [blob, setBlob] = useState<null | Blob>(null);
  const [attachmentUri, setAttachmentUri] = useState<string | null>(null);
  const [isDownload, setIsDownload] = useState(false);
  const [isNotSupported, setIsNotSupported] = useState(false);
  const [showInstallPlugin, setShowInstallPlugin] = useState(false);

  const [attachment, setAttachment] = useState<null | IAttachment>(null);

  const { setId, setProgress } = useDownloadFileStore();

  const addBlob = (blob: Blob, fileName?: string) => {
    if (fileName) {
      if (isPDF(fileName)) {
        setBlob(blob);
        return;
      }

      createPreviewFromPlugin(blob, {
        ContentType: blob.type,
        Id: "",
        FileName: "",
        Kind: 1,
      }).then((b) => setBlob(b));
      return;
    }

    if (blob.type !== "application/pdf") {
      createPreviewFromPlugin(blob, {
        ContentType: blob.type,
        Id: "",
        FileName: "",
        Kind: 1,
      }).then((b) => setBlob(b));

      return;
    }

    setBlob(blob);
  };

  const removeBlob = () => {
    setBlob(null);
    setAttachmentUri(null);
    setId(null);
    setProgress(0);
  };

  const isPDF = (fileName: string) => {
    const extension = fileName.split(".").at(-1);
    return extension.toLocaleLowerCase() === "pdf";
  };

  const checkIsAllowed = (fileName: string) => {
    fileName = fileName.toLocaleLowerCase();
    const extension = fileName.split(".").at(-1);
    const isAllowed = isAllowedAttachment(extension ?? "no");

    return (isAllowed && !withoutPlugin) || isPDF(fileName);
  };

  const checkIsArchive = (fileName: string) => {
    fileName = fileName.toLocaleLowerCase();
    const extension = fileName.split(".").at(-1);
    const isAllowed = isArchiveAttachment(extension ?? "no");

    return isAllowed;
  };

  const createPreviewFromPlugin = async (
    blob: Blob,
    attachment: Partial<IAttachment>,
  ): Promise<Blob> => {
    setShowInstallPlugin(false);
    const result = await new Promise<Blob>((resolve, reject) => {
      getPluginVersion(
        {},
        {
          onSuccess: async () => {
            const base64: string = (await blobToBase64(blob)) as string;
            console.log("1");
            tryPreview(
              {
                AttachmentId: attachment.Id,
                Content: base64.split(",")[1],
                ContentType: attachment.ContentType,
                DocumentId: attachment.Id,
                FileName: attachment.FileName,
                Kind: attachment.Kind,
              },
              {
                onSuccess: async (response) => {
                  console.log("2");
                  const c = response.data.Content;
                  const buffer = await readFileFromBase64(
                    c,
                    "application/other",
                  );
                  setId(null);
                  setProgress(0);

                  resolve(new Blob([buffer]));
                },
                onError: () => {
                  setId(null);
                  setProgress(0);

                  reject();
                },
              },
            );
          },
          onError: () => {
            setShowInstallPlugin(true);

            reject("noplugin");
          },
        },
      );
    });

    return result;
  };

  const fetchFile = async (
    attachment: Partial<IAttachment>,
    packageKind?: number,
  ): Promise<string> => {
    console.log("package kind: ", packageKind);
    return new Promise((resolve) => {
      if (!chatToken) {
        getFile(
          {
            id: attachment.Uri,
            ownerKind: packageKind ?? attachment.Kind,
          },
          {
            onSuccess: (data) => {
              resolve(data.data.Url);
            },
          },
        );
      } else {
        getContactFromChatFile(
          {
            id: attachment.Uri,
            ownerKind: packageKind ?? attachment.Kind,
            token: chatToken,
          },
          {
            onSuccess: (data) => {
              resolve(data.data.Url);
            },
          },
        );
      }
    });
  };

  const decryptSecuredPackage = async (
    securedBlob: any,
    attachment: Partial<IAttachment>,
    isDownloading: boolean = false,
  ): Promise<Blob> => {
    const result = await new Promise<Blob>(async (resolve, reject) => {
      console.log("decrypt secured package");
      const key = keys.find(
        (key: IKey) => key?.Id === attachment?.EncryptionKeyId,
      );

      console.log(keys);
      if (!key) {
        console.error("KeyId", attachment?.EncryptionKeyId);
        console.error("Keys collection", keys);

        reject("nokey");
      }

      console.log("key founded");

      let parentKey: null | IKey | undefined = null;

      if (key?.ParentKeyId) {
        console.log("is parent key");
        parentKey = keys.find((kp: IKey) => kp?.Id === key.ParentKeyId);
      }

      console.log("key and parentkeuy", key, parentKey);

      try {
        console.log("trying dfecrypt");
        const decryptedBlob = await decryptFile(
          userId,
          new Blob([securedBlob.data]),
          key,
          parentKey,
          chatId,
        );

        console.log("done");
        if (!isPDF(attachment.FileName) && !isDownloading) {
          if (attachment.FileName.search("ics") !== -1) {
            resolve(decryptedBlob);
            return;
          }
          console.log("Trying to convert to PDF");
          createPreviewFromPlugin(decryptedBlob, attachment)
            .then((r) => resolve(r))
            .catch((r) => reject(r));
          return;
        }

        resolve(decryptedBlob);
      } catch (e) {
        console.log("SECURE DECRYPT FAILED", e);
        reject(e);
      } finally {
        setId(null);
        setProgress(0);
      }
    });

    return result;
  };

  const decryptPackage = async (
    attachment: Partial<IAttachment>,
    packageKind?: number,
    isDownloading: boolean = false,
  ): Promise<Blob> => {
    const result = await new Promise<Blob>(async (resolve, reject) => {
      console.log("decrypt package: ", attachment, packageKind);
      //setAttachmentUri(attachment.Uri);
      const fileURL: string = await fetchFile(attachment, packageKind);
      console.log("mam file");
      const securedBlob = await getBlob(fileURL, (progress) => {
        const percentCompleted = Math.round(
          (progress.loaded * 100) / (progress.total ?? attachment.Size),
        );
        console.log("a", percentCompleted, progress);
        setProgress(percentCompleted);
      });
      if (!fileURL) {
        console.log("File not found");
        reject("File not found");
      }

      if (
        !attachment.EncryptionKeyId ||
        attachment.EncryptionKeyId === "null"
      ) {
        console.log("nie ma klucza");
        if (!isPDF(attachment.FileName) && !isDownloading) {
          console.log("plugin");
          createPreviewFromPlugin(new Blob([securedBlob.data]), attachment)
            .then((r) => resolve(r))
            .catch((r) => reject(r));
          return;
        }

        console.log("nie ma klucza blob");
        setId(null);
        setProgress(0);

        resolve(new Blob([securedBlob.data]));
        return;
      }

      console.log("asdf");

      decryptSecuredPackage(securedBlob, attachment, isDownloading)
        .then((x) => resolve(x))
        .catch((r) => reject(r));
    });

    return result;
  };

  const decryptPackageAndSetBlob = async (
    attachment: Partial<IAttachment>,
    packageKind?: number,
  ) => {
    decryptPackage(attachment, packageKind, false).then((b) => setBlob(b));
  };

  const downloadCourtPortalPdf = async (
    attachmentId: string,
    fileName: string,
    isDownload: boolean,
  ) => {
    setProgress(0);

    getCourtPortalPreview(
      {
        id: attachmentId,
        callback: (progress) => {
          const percentCompleted = Math.round(
            (progress.loaded * 100) / progress.total,
          );
          setProgress(percentCompleted);
        },
      },
      {
        onSuccess(data) {
          console.log("Success", data.headers);
          const ct = data.headers["content-type"];

          if (ct === "application/rtf" && !isDownload) {
            createPreviewFromPlugin(new Blob([data.data]), {
              ContentType: "application/rtf",
              Id: attachmentId,
              FileName: "podglad.rtf",
              Kind: 2,
            }).then((b) => setBlob(b));
          } else {
            if (isDownload) {
              const URL = window.URL.createObjectURL(new Blob([data.data]));
              const link = document.createElement("a");
              link.href = URL;
              link.setAttribute(
                "download",
                ct === "application/rtf" ? `${fileName}.rtf` : fileName,
              );
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(URL);
            } else {
              setBlob(new Blob([data.data]));
            }
          }
          setId(null);
        },
        onError() {
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd pobierania podglądu",
            text: "Dokument nie posiada wizualizacji na serwerze Portalu Informacyjnego",
          });
        },
      },
    );
    // const blob = await getBlob(
    //   `/api/courtPortal/preview?id=${attachmentId}`,
    //   (progress) => {
    //
    //   }
    // );
  };

  const downloadKsefPdf = async (attachmentId: string) => {
    setProgress(0);

    getKsefPreview(
      {
        id: attachmentId,
        callback: (progress) => {
          const percentCompleted = Math.round(
            (progress.loaded * 100) / progress.total,
          );
          setProgress(percentCompleted);
        },
      },
      {
        onSuccess(data) {
          console.log("Success", data.headers);

          setBlob(new Blob([data.data]));
          setId(null);
          setIsLoading(null);
        },
        onError() {
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd pobierania podglądu",
            text: "Dokument nie posiada wizualizacji na KSeF",
          });
        },
      },
    );
    // const blob = await getBlob(
    //   `/api/courtPortal/preview?id=${attachmentId}`,
    //   (progress) => {
    //
    //   }
    // );
  };

  const showNotSupportedNotification = () => {
    notification({
      type: ENotificationType.ERROR,
      title: "Nie można wyświetlić poglądu",
      text: "Przepraszamy! aktualnie nie obsługujemy tego formatu pliku. Użyj opcji Pobierz by pobrać plik w jego oryginalnym formacie.",
    });
  };

  const create = async (
    ownerId: string,
    attachment: IAttachment,
    kind: number,
  ) => {
    const isAllowed = checkIsAllowed(attachment.FileName);
    setIsNotSupported(!isAllowed);

    console.log("[usePDF] create, isAllowed=", isAllowed);

    if (!isAllowed) {
      showNotSupportedNotification();
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setBlob(null);
    setId(attachment.Id);
    setAttachment(attachment);
    setProgress(0);

    if (
      kind === ItemKind.CourtPortalDeliverance ||
      kind === ItemKind.CourtPortalDeliveranceDocument ||
      kind === ItemKind.CourtPortalDocument
    ) {
      downloadCourtPortalPdf(attachment.Id, attachment.FileName, false);
      setIsLoading(false);
      return;
    }

    if (kind === ItemKind.KSeFInvoice) {
      downloadKsefPdf(attachment.Id);
      return;
    }

    console.log("deszyfruje");

    const blob = await decryptPackage(attachment, kind, false);
    setBlob(blob);

    setIsLoading(false);
  };

  const download = async (
    ownerId: string,
    attachment: IAttachment,
    kind: number,
    onFinish?: (b: Blob) => void,
  ) => {
    setId(attachment.Id);
    setProgress(0);

    if (
      kind === ItemKind.CourtPortalDeliverance ||
      kind === ItemKind.CourtPortalDeliveranceDocument ||
      kind === ItemKind.CourtPortalDocument
    ) {
      downloadCourtPortalPdf(attachment.Id, attachment.FileName, true);
      return;
    }

    console.log("deszyfruje");

    const blob = await decryptPackage(attachment, kind, true);

    onFinish?.(blob);

    const URL = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", attachment.FileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(URL);
  };

  return {
    attachmentUri,
    attachment,
    create,
    download,
    isLoading,
    blob,
    addBlob,
    removeBlob,
    decryptPackage,
    decryptPackageAndSetBlob,
    isDownload,
    setIsDownload,
    isNotSupported,
    checkIsAllowed,
    checkIsArchive,
    showNotSupportedNotification,
    setIsNotSupported,
    downloadCourtPortalPdf,
    showInstallPlugin,
    setShowInstallPlugin,
  };
}

export default usePDF;
